import { useContext, useEffect, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { Link } from "react-router-dom";
import SpecialistApi from "../../api/specialistsApi";
import UsersApi from "../../api/usersApi";
import { ReactComponent as AllertCircle } from "../../assets/General/alert-circle.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/General/chevronDown.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/Specialists/chevronLeft.svg";
import { ReactComponent as FilterIcon } from "../../assets/Specialists/filter-icon.svg";
import MagnifierIcon from "../../assets/Specialists/magnifier.svg";
import { ReactComponent as VerifyIcon } from "../../assets/Specialists/verify.svg";
import CircleAvatar from "../../components/CircleAvatar/CircleAvatar";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import SpecialistTable from "../../components/SpecialistTable/SpecialistTable";
import { ContextProvider } from "../../contextProvider";
import { SessionComment } from "../../types/TypeSession";
import {
  SpecialistData,
  TypeSpecialistFilter,
} from "../../types/TypeSpecialist";
import { TypeUserData } from "../../types/TypeUsers";
import { getUniqueCountriesData } from "../../utils/catalogUtils";
import { formatTimeZoneWithGMT } from "../../utils/dateActions";
import { calculateAge } from "../../utils/general";
import { getTranslation } from "../../utils/getTranslation";
import Profile from "../Profile/Profile";
import CommentCard from "./CommentCard/CommentCard";
import ServicesConfigs from "./ServicesConfigs/ServicesConfigs";
import SpecialistSettings from "./SpecialistSettings/SpecialistSettings";
import s from "./Specialists.module.css";

const statusSelectData = [
  { value: undefined, lable: "All" },
  { value: true, lable: "Verified" },
  { value: false, lable: "Unverified" },
];

const statusRequestSelectedData = [
  { value: undefined, lable: "All" },
  {
    value: {
      verifyProfileRequest: undefined,
      servicesRequest: true,
      reviewRequested: undefined,
    },
    lable: "Services requests",
  },
  {
    value: {
      verifyProfileRequest: true,
      servicesRequest: undefined,
      reviewRequested: undefined,
    },
    lable: "Unverified changes",
  },
  {
    value: {
      verifyProfileRequest: undefined,
      servicesRequest: undefined,
      reviewRequested: true,
    },
    lable: "Ready for review",
  },
];

type PickerData = { value: string; lable: string; disabled?: boolean };

type FiltersDataType = {
  mainSpecializations: PickerData[];
  subSpecializations: PickerData[];
  genders: PickerData[];
  levels: PickerData[];
  tags: PickerData[];
  languages: PickerData[];
  countries: PickerData[];
  cities: PickerData[];
  age: PickerData[];
};

const Specialists = () => {
  const token = localStorage.getItem("token");
  const { userData, setUserIdForCreate } = useContext(ContextProvider);
  const filterRef = useRef<any>(null);
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [specialistData, setSpecialistData] = useState<
    TypeUserData[] | undefined
  >();
  const [page, setPage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [verifiedStatus, setVerifiedStatus] = useState<boolean | undefined>(
    undefined,
  );
  const [verificationRequests, setVerificationRequests] = useState<
    | {
        verifyProfileRequest: boolean | undefined;
        servicesRequest: boolean | undefined;
        reviewRequested: boolean | undefined;
      }
    | undefined
  >();
  const [commentsData, setCommentsData] = useState<SessionComment[]>([]);
  const [filtersCollapsed, setFiltersCollapsed] = useState({
    statusFilter: true,
    requestFilter: true,
  });
  const [selectedSpecialist, setSelectedSpecialist] = useState<
    TypeUserData | undefined
  >();
  const [selectedSpecialityInfo, setSelectedSpecialityInfo] = useState<
    SpecialistData | undefined
  >();
  const [selectNavigationElement, setSelectNavigationElement] = useState<
    "info" | "complaints" | "settings" | "services"
  >("info");

  const [avatars, setAvatars] = useState<{ [userId: string]: string }>({});
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isUpdatedProfileModalOpen, setIsUpdatedProfileModalOpen] =
    useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filterUpdateCounter, setFilterUpdateCounter] = useState(0);

  const [filters, setFilters] = useState<TypeSpecialistFilter>({
    specialistId: {},
  });
  const [filtersData, setFiltersData] = useState<FiltersDataType>({
    mainSpecializations: [],
    subSpecializations: [],
    genders: [],
    levels: [],
    tags: [],
    languages: [],
    countries: [],
    age: [],
    cities: [],
  });
  const [shuffleSeed, setShuffleSeed] = useState<string>();

  const handleClickOutside = (event: any) => {
    const isInsideFilterMenu =
      filterRef.current && filterRef.current.contains(event.target);
    const isInsideSelect = event.target.closest(".multi-select-menu");
    const isInsideSelect2 = event.target.closest(".MuiModal-backdrop");

    if (
      !isInsideFilterMenu &&
      !isInsideSelect &&
      !isInsideSelect2 &&
      !event.target.classList.contains(s.menuIcon)
    ) {
      setIsFilterOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const makeAsync = async () => {
      if (token && specialistData && specialistData.length > 0) {
        const promises = specialistData.map((spec) =>
          UsersApi.getUserPublicAvatarAndName(spec._id),
        );
        const responses = await Promise.all(promises);
        const avatarsData = responses.reduce(
          (acc, response) => {
            const avatar = response.avatarS3 || response.avatar;
            if (avatar) {
              acc[response._id] = avatar;
            }
            return acc;
          },
          {} as { [userId: string]: string },
        );

        setAvatars(avatarsData);
      }
    };
    makeAsync();
  }, [specialistData]);

  useEffect(() => {
    const makeAsync = async () => {
      if (token && userData) {
        const payload = {
          filters: {
            ...filters,
            name: search,
            email: search,
            specialistId: {
              ...filters.specialistId,
              isVerified: verifiedStatus,
              isVerifiedChanges:
                verificationRequests === undefined ||
                verificationRequests.verifyProfileRequest === undefined
                  ? undefined
                  : !verificationRequests.verifyProfileRequest,
              servicesConfig:
                verificationRequests === undefined ||
                verificationRequests.servicesRequest === undefined
                  ? undefined
                  : {
                      reviewRequested: verificationRequests.servicesRequest,
                    },
              reviewRequested:
                verificationRequests === undefined ||
                verificationRequests.reviewRequested === undefined
                  ? undefined
                  : verificationRequests.reviewRequested,
            },
            page: page + 1,
            limit: rowsPerPage,
            sort: {
              field: "_id",
              type: -1 as -1,
            },
          },
          shuffleSeed: shuffleSeed,
        };

        setIsLoader(true);

        const specialistsResponse = SpecialistApi.getFilteredSpecialists(
          token,
          payload,
        );
        const availableParamsRes = SpecialistApi.getFilterAvailableParams(
          token,
          {
            filters: payload.filters,
          },
        );
        const [response, availableParams] = await Promise.all([
          specialistsResponse,
          availableParamsRes,
        ]);

        if (response.users && !response.users.length) {
          setSpecialistData(undefined);
        }
        if (response.status && response.users) {
          setSpecialistData(response.users);
          setShuffleSeed(response.shuffleSeed);
        }
        setIsLoader(false);

        if (availableParams.status && availableParams.paramsAvailable) {
          const params = availableParams.paramsAvailable;
          setTotalAmount(params.totalAmount);
          const mainSpecData =
            params.mainSpecializations?.map((item) => ({
              value: item.mainSpecialization.specialization,
              lable: getTranslation(item.mainSpecialization.labels, "en"),
              disabled: !item.choosable,
            })) ?? [];
          const tags = params.tags
            ? params.tags.map((item) => ({
                value: item.tag._id,
                lable: getTranslation(item.tag.labels, "en"),
                disabled: !item.choosable,
              }))
            : [];
          const levels = params.levels
            ? params.levels.levels.map((item) => ({
                value: item.level._id,
                lable:
                  getTranslation(item.level.title, "en") +
                  " ~ " +
                  item.level.avgPrice.toFixed(0) +
                  "$",
                disabled: !item.choosable,
              }))
            : [];
          const subSpec = params.subSpecializations
            ? params.subSpecializations?.map((item) => ({
                value: item.subSpecialization.specialization,
                lable: getTranslation(item.subSpecialization.labels, "en"),
                disabled: !item.choosable,
              }))
            : [];
          const languages = params.preferredLanguages
            ? params.preferredLanguages?.map((item) => ({
                value: item.language,
                lable: item.language,
                disabled: !item.choosable,
              }))
            : [];
          const countries = params.locations
            ? getUniqueCountriesData(params.locations).map((item) => ({
                value: item.location.split(", ")[1],
                lable: item.location.split(", ")[1],
                disabled: !item.choosable,
              }))
            : [];

          const genders = params.genders
            ? params.genders.map((item) => ({
                value: item.gender,
                lable: item.gender === "male" ? "Male" : "Female",
                disabled: !item.choosable,
              }))
            : [];
          const cities = params.locations
            ? getUniqueCountriesData(params.locations).map((item) => ({
                value: item.location.split(", ")[0],
                lable: item.location.split(", ")[0],
                disabled: !item.choosable,
              }))
            : [];
          setFiltersData({
            age: [],
            countries: countries,
            genders: genders,
            languages: languages,
            levels: levels,
            mainSpecializations: mainSpecData,
            subSpecializations: subSpec,
            tags: tags,
            cities: cities,
          });
        }
        if (response.users && !response.users.length) return;
      }
    };
    makeAsync();
  }, [filterUpdateCounter, page, rowsPerPage]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilterUpdateCounter((prev) => prev + 1);
      setPage(0);
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setDebouncedSearch(value);
  };

  const selectSpecialistHandler = (specialistId: string) => {
    if (!specialistData) return;
    const selectedSpec = specialistData.filter(
      (item) => item._id === specialistId,
    )[0];
    setSelectedSpecialist(selectedSpec);
  };

  useEffect(() => {
    if (!token || !selectedSpecialist) return;
    if (
      !selectedSpecialityInfo ||
      (selectedSpecialityInfo &&
        !selectedSpecialist
          .specialistIds!.map((item) => item._id)
          .includes(selectedSpecialityInfo._id))
    ) {
      setSelectedSpecialityInfo(selectedSpecialist.specialistIds![0]);
    }
    const makeAsync = async () => {
      const commentsResponse = await SpecialistApi.getSpecialistsComments(
        selectedSpecialist._id,
        100,
        1,
        selectedSpecialityInfo?._id,
      );

      if (commentsResponse.status)
        setCommentsData(
          commentsResponse.comments.filter(
            (el) => el.customComment.length || el.defaultComments.length,
          ),
        );
    };
    makeAsync();
  }, [selectedSpecialist]);

  return (
    <div className={s.container}>
      <div className={s.headBlock}>
        <div className={s.pageInfoTitleBlock}>
          <h1>Specialists</h1>
        </div>
      </div>
      <div className={s.filterBlock}>
        <div className={s.searchInputBlock}>
          <input
            type="text"
            className={s.searchInput}
            placeholder="Search..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <img src={MagnifierIcon} alt="" className={s.magnifierIcon} />
        </div>
        <div className={s.filterButton} ref={filterRef}>
          <div
            className={isFilterOpen ? s.rectangle : s.circle}
            onClick={() => setIsFilterOpen(true)}
          >
            {isFilterOpen ? (
              <div className={s.inputsFilterBlock}>
                <div className={s.columnFilters}>
                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.countries}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          country:
                            value === "" ? undefined : ([value] as string[]),
                        }));
                      }}
                      value={filters.country ?? ""}
                      multiplie={false}
                      lable={"Country"}
                      resetBtn
                    />
                  </div>
                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.levels}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          specialistId: {
                            ...prev.specialistId,
                            level:
                              value === "" ? undefined : ([value] as string[]),
                          },
                        }));
                      }}
                      value={filters.specialistId?.level?.[0] ?? ""}
                      multiplie={false}
                      lable={"Level"}
                      resetBtn
                    />
                  </div>
                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.languages}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          preferredLanguages: value as string[],
                        }));
                      }}
                      value={filters.preferredLanguages ?? []}
                      multiplie={true}
                      lable={"Language"}
                      checkBox
                    />
                  </div>
                </div>
                <div className={s.columnFilters}>
                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.mainSpecializations}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          specialistId: {
                            ...prev.specialistId,
                            mainSpecializations:
                              value === "" ? undefined : ([value] as string[]),
                          },
                        }));
                      }}
                      value={filters.specialistId?.mainSpecializations ?? []}
                      multiplie={false}
                      lable={"Main specialization"}
                      resetBtn
                    />
                  </div>

                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.subSpecializations}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          specialistId: {
                            ...prev.specialistId,
                            subSpecializations: value as string[],
                          },
                        }));
                      }}
                      value={filters.specialistId?.subSpecializations ?? []}
                      multiplie={true}
                      checkBox
                      lable={"Sub specialization"}
                    />
                  </div>

                  <div className={s.inputFilter}>
                    <MultipleSelect
                      data={filtersData.genders}
                      setValue={(value) => {
                        setFilterUpdateCounter((prev) => prev + 1);
                        setPage(0);
                        setFilters((prev) => ({
                          ...prev,
                          gender:
                            value === "" ? undefined : ([value] as string[]),
                        }));
                      }}
                      value={filters.gender ?? ""}
                      multiplie={false}
                      lable={"Gender"}
                      resetBtn
                    />{" "}
                  </div>
                </div>
              </div>
            ) : (
              <FilterIcon className={s.filterIcon} />
            )}
          </div>
        </div>
        <div className={s.leftFiltersBlock}>
          <div style={{ zIndex: 99 }}>
            <span>Verification requests</span>
            <div className={s.verifiedStatusFilterBlock}>
              <div
                className={s.selectedStatusBlock}
                onClick={(e) => {
                  e.stopPropagation();
                  setFiltersCollapsed((prev) => ({
                    ...prev,
                    requestFilter: !prev.requestFilter,
                  }));
                }}
                style={
                  filtersCollapsed.requestFilter
                    ? {}
                    : {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        paddingBottom: "3px",
                      }
                }
              >
                <span>
                  {verificationRequests === undefined
                    ? "All"
                    : verificationRequests.servicesRequest === true
                      ? "Services request"
                      : verificationRequests.reviewRequested
                        ? "Ready for review"
                        : "Unverified changes"}
                </span>{" "}
                <ChevronDownIcon
                  className={
                    filtersCollapsed.requestFilter
                      ? s.chevronDownIcon
                      : s.chevronUpIcon
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersCollapsed((prev) => ({
                      ...prev,
                      requestFilter: !prev.requestFilter,
                    }));
                  }}
                />
              </div>
              <AnimateHeight
                height={filtersCollapsed.requestFilter ? 0 : "auto"}
                duration={500}
                className={s.animHeight}
              >
                {statusRequestSelectedData.map((item, index) => (
                  <span
                    key={index}
                    onClick={() => {
                      setFilterUpdateCounter((prev) => prev + 1);
                      setPage(0);
                      setVerificationRequests(item.value);
                      setFiltersCollapsed((prev) => ({
                        ...prev,
                        requestFilter: !prev.requestFilter,
                      }));
                    }}
                  >
                    {item.lable}
                  </span>
                ))}
              </AnimateHeight>
            </div>
          </div>
          <div style={{ zIndex: 99 }}>
            <span>Status</span>
            <div className={s.verifiedStatusFilterBlock}>
              <div
                className={s.selectedStatusBlock}
                onClick={(e) => {
                  e.stopPropagation();
                  setFiltersCollapsed((prev) => ({
                    ...prev,
                    statusFilter: !prev.statusFilter,
                  }));
                }}
                style={
                  filtersCollapsed.statusFilter
                    ? {}
                    : {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        paddingBottom: "3px",
                      }
                }
              >
                <span>
                  {verifiedStatus === undefined
                    ? "All"
                    : verifiedStatus === false
                      ? "Unverified"
                      : "Verified"}
                </span>{" "}
                <ChevronDownIcon
                  className={
                    filtersCollapsed.statusFilter
                      ? s.chevronDownIcon
                      : s.chevronUpIcon
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersCollapsed((prev) => ({
                      ...prev,
                      statusFilter: !prev.statusFilter,
                    }));
                  }}
                />
              </div>
              <AnimateHeight
                height={filtersCollapsed.statusFilter ? 0 : "auto"}
                duration={500}
                className={s.animHeight}
              >
                {statusSelectData.map((item, index) => (
                  <span
                    key={index}
                    onClick={() => {
                      setFilterUpdateCounter((prev) => prev + 1);
                      setPage(0);
                      setVerifiedStatus(item.value);
                      setFiltersCollapsed((prev) => ({
                        ...prev,
                        statusFilter: !prev.statusFilter,
                      }));
                    }}
                  >
                    {item.lable}
                  </span>
                ))}
              </AnimateHeight>
            </div>
          </div>
        </div>
      </div>
      <div className={s.contentBlock}>
        <div className={s.tableBlock}>
          {specialistData && specialistData.length ? (
            <SpecialistTable
              rows={specialistData.map((item, index) => ({
                id: item._id,
                name:
                  getTranslation(item.name, "en") +
                  " " +
                  getTranslation(item.surname, "en"),
                email: item.email,
                specialization: item.specialistIds!.map(
                  (item) => item.mainSpecializations[0]?.specialization,
                ),
                status: item.specialistIds![0].isVerified,
                reviewRequested: item
                  .specialistIds!.map((spec) => spec.reviewRequested)
                  .includes(true),
                serviceReviewRequested: item
                  .specialistIds!.map(
                    (item) => item.servicesConfig.reviewRequested,
                  )
                  .includes(true),
              }))}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              selectSpecialistHandler={selectSpecialistHandler}
              count={totalAmount}
            />
          ) : isLoader ? (
            <div className={s.noSpecialistBlock}>
              <Loader size={80} />{" "}
            </div>
          ) : (
            <div className={s.noSpecialistBlock}>
              <h1>No specialists found</h1>
            </div>
          )}
        </div>
        {specialistData && specialistData.length ? (
          <div
            className={`${s.specialistInfoBlock} ${
              selectedSpecialist && s.specialistInfoBlockActive
            }`}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                className={s.backBlock}
                role="button"
                onClick={() => setSelectedSpecialist(undefined)}
              >
                <ChevronLeftIcon className={s.chevronBackIcon} />
                Back
              </div>
              {selectedSpecialist && (
                <Link
                  to={`/chat?userId=${encodeURIComponent(
                    selectedSpecialist._id,
                  )}`}
                  onClick={() => setUserIdForCreate(selectedSpecialist._id)}
                  className={s.sendMessageBtn}
                >
                  Send message
                </Link>
              )}
            </div>

            <div className={s.headNameBlock}>
              {selectedSpecialist && (
                <CircleAvatar
                  name={getTranslation(selectedSpecialist?.name)}
                  surname={getTranslation(selectedSpecialist?.surname)}
                  avatarProps={avatars[selectedSpecialist._id]}
                />
              )}
              <span>
                {getTranslation(selectedSpecialist?.name) +
                  " " +
                  getTranslation(selectedSpecialist?.surname)}
              </span>
              {selectedSpecialityInfo?.isVerified && <VerifyIcon />}
            </div>

            {selectedSpecialist &&
              selectedSpecialist.specialistIds!.length > 1 && (
                <div className={s.specialityTabsBlock}>
                  {selectedSpecialist.specialistIds?.map((item, index) => (
                    <div
                      key={index}
                      className={
                        selectedSpecialityInfo?._id === item._id
                          ? s.activeChooseSpecTab
                          : s.chooseSpecTab
                      }
                      onClick={() => setSelectedSpecialityInfo(item)}
                    >
                      {item.mainSpecializations[0].specialization}
                      {(!item.isVerifiedChanges ||
                        item.servicesConfig.reviewRequested) && (
                        <AllertCircle width={20} height={20} />
                      )}
                    </div>
                  ))}
                </div>
              )}

            <div className={s.navigateSpecInfoBlock}>
              <div
                className={`${s.navElement} ${
                  selectNavigationElement === "info" && s.selectNavElement
                }`}
                onClick={() => setSelectNavigationElement("info")}
              >
                Info
              </div>
              <div
                className={`${s.navElement} ${
                  selectNavigationElement === "complaints" && s.selectNavElement
                }`}
                onClick={() => setSelectNavigationElement("complaints")}
              >
                Complaints
              </div>
              <div
                className={`${s.navElement} ${
                  selectNavigationElement === "settings" && s.selectNavElement
                }`}
                onClick={() => setSelectNavigationElement("settings")}
              >
                Settings
                {!selectedSpecialityInfo?.isVerifiedChanges && (
                  <AllertCircle
                    className={s.allowedIcon}
                    width={20}
                    height={20}
                  />
                )}
              </div>
              <div
                className={`${s.navElement} ${
                  selectNavigationElement === "services" && s.selectNavElement
                }`}
                onClick={() => setSelectNavigationElement("services")}
              >
                Services
                {selectedSpecialityInfo?.servicesConfig.reviewRequested && (
                  <AllertCircle
                    className={s.allowedIcon}
                    width={20}
                    height={20}
                  />
                )}
              </div>
              <div className={s.underLine}></div>
            </div>
            {selectNavigationElement === "info" ? (
              <div className={s.mainSpecInfoBlock}>
                <div className={s.personInfoTitleBlock}>
                  <div></div>
                  <h2 className={s.personInfoTitle}>Personal Information</h2>
                  <div
                    onClick={() => setIsProfileModalOpen((prev) => !prev)}
                    role="button"
                    className={s.viewAllBtn}
                  >
                    View all
                  </div>
                </div>
                <div className={s.personInfoBlock}>
                  <div className={s.infoElement}>
                    <span>Verified</span>
                    <span>
                      {selectedSpecialityInfo?.isVerified ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className={s.infoElement}>
                    <span>First Name</span>
                    <span>{getTranslation(selectedSpecialist?.name)}</span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Last Name</span>
                    <span>{getTranslation(selectedSpecialist?.surname)}</span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Email</span>
                    <span>{selectedSpecialist?.email}</span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Specialization</span>
                    <span>
                      {selectedSpecialityInfo?.mainSpecializations[0] &&
                        selectedSpecialityInfo?.mainSpecializations[0]
                          .specialization}
                    </span>
                  </div>
                  <div className={s.infoElement}>
                    <span>State</span>
                    <span>
                      {selectedSpecialist?.location?.includes("undefined")
                        ? "-"
                        : selectedSpecialist?.location}
                    </span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Age</span>
                    <span>
                      {calculateAge(new Date(selectedSpecialist?.dateOfBirth!))}
                    </span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Time zone</span>
                    <span>
                      {selectedSpecialist?.timezone
                        ? formatTimeZoneWithGMT(selectedSpecialist?.timezone)
                        : "-"}
                    </span>
                  </div>
                  <div className={s.infoElement}>
                    <span>Years experience</span>
                    <span>
                      {selectedSpecialityInfo?.practiceStartYear
                        ? new Date().getFullYear() -
                          new Date(
                            selectedSpecialityInfo?.practiceStartYear,
                          ).getFullYear() +
                          " year(s)"
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            ) : selectNavigationElement === "complaints" ? (
              <div className={s.complaintsBlock}>
                {commentsData && commentsData.length ? (
                  commentsData.map((item, index) => (
                    <CommentCard
                      key={index}
                      id={item.commentBy}
                      commentMessage={item.customComment}
                      staticComments={item.defaultComments}
                      date={new Date(item.date).toISOString()}
                    />
                  ))
                ) : (
                  <h2>No Complaints</h2>
                )}
              </div>
            ) : selectNavigationElement === "services" ? (
              selectedSpecialityInfo && (
                <ServicesConfigs
                  specialistData={selectedSpecialityInfo}
                  setSpecialistData={setSpecialistData}
                  setSeletedSpecialityInfo={setSelectedSpecialityInfo}
                  setSelectedSpecialist={setSelectedSpecialist}
                />
              )
            ) : (
              selectedSpecialist && (
                <SpecialistSettings
                  setSeletedSpecialityInfo={setSelectedSpecialityInfo}
                  selectedSpecialist={selectedSpecialityInfo}
                  setIsUpdatedProfileModalOpen={setIsUpdatedProfileModalOpen}
                  setSelectedSpecialist={setSelectedSpecialist}
                  setSpecialistData={setSpecialistData}
                  getPublicInfoString={`${selectedSpecialist.latinName}-${
                    selectedSpecialist.latinSurname
                  }-${selectedSpecialist._id.slice(-7)}`}
                  userId={selectedSpecialist._id}
                  setIsPublicProfileModalOpen={setIsProfileModalOpen}
                />
              )
            )}
          </div>
        ) : (
          ""
        )}
      </div>
      <ModalWindow
        isOpen={isProfileModalOpen}
        setIsOpen={setIsProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0"
      >
        <div className={s.profileContainer}>
          {selectedSpecialist && (
            <Profile
              userId={selectedSpecialist._id}
              isOpen={isProfileModalOpen}
              setIsOpen={setIsProfileModalOpen}
              getPublicInfoString={`${selectedSpecialist.latinName}-${
                selectedSpecialist.latinSurname
              }-${selectedSpecialist._id.slice(-7)}`}
              isPublicProfile
            />
          )}
        </div>
      </ModalWindow>
      <ModalWindow
        isOpen={isUpdatedProfileModalOpen}
        setIsOpen={setIsUpdatedProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0"
      >
        <div className={s.profileContainer}>
          {selectedSpecialist && (
            <Profile
              userId={selectedSpecialist._id}
              isOpen={isUpdatedProfileModalOpen}
              setIsOpen={setIsUpdatedProfileModalOpen}
              getPublicInfoString={`${selectedSpecialist.latinName}-${
                selectedSpecialist.latinSurname
              }-${selectedSpecialist._id.slice(-7)}`}
              isPublicProfile={false}
              initialSpecialization={selectedSpecialist._id}
            />
          )}
        </div>
      </ModalWindow>
    </div>
  );
};

export default Specialists;
